// Composer Studio — 작성(writeForm) 탭 편집 툴바
//
// 작성 탭 상단 전역 반응형 바 바로 아래에 얹히는 얇은 편집 툴바.
// 양식 탭 EditorToolbar와 별개(작성 탭 위젯 선택/그룹 모델에 맞춘 경량 버전).
//   [↶ 실행취소][↷ 다시실행] │ [선택 N개] [⊞ 섹션으로 묶기][섹션 해제][삭제][선택 해제]
//
// 동작은 전부 EditorPage의 기존 핸들러/단축키와 공유 — 새 로직 없이 버튼만 배선한다.
// 활성 조건:
//   - undo/redo   : canUndo / canRedo
//   - 묶기(Ctrl+G): selectionCount>=2 && canGroup(같은 부모 형제)
//   - 해제(Ctrl+Shift+G): isGroupSelected(단일 group 선택)
//   - 삭제(Delete) / 선택 해제(Esc): selectionCount>=1
//   - 위젯 자동 배치: 버튼 → 소형 선택 메뉴(ComposerContextMenu 재사용 — body portal이라
//     툴바 overflow-x-auto에 잘리지 않음). 채우기=즉시, 전체 재구성=EditorPage 확인 모달 경유.
//     그 버튼 옆에 매뉴얼 `?`(HelpLink `writeFormAutoLayout`) — 두 모드의 차이와 「표 칸은 위젯
//     이름표를 숨긴다」는 성질이 버튼 title에 담기지 않아 문서로 보낸다. 새 탭이라 편집 상태 보존.

import { useState } from 'react';
import { ComposerContextMenu } from './ComposerContextMenu';
import { HelpLink } from './HelpLink';

interface WriteFormToolbarProps {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  /** 현재 다중선택 위젯 개수(0=없음, 1=단일, 2+=묶기 후보). */
  selectionCount: number;
  /** 선택 위젯들이 같은 부모의 형제라 묶기 가능한지(2개+ 전제). */
  canGroup: boolean;
  /** 단일 선택 위젯이 섹션(group)이라 해제 가능한지. */
  isGroupSelected: boolean;
  onGroup: () => void;
  onUngroup: () => void;
  onRemove: () => void;
  onClearSelection: () => void;
  /** 캔버스 선택 방식 — 'isolation'(그룹 우선) | 'deepest'(최심부 우선). 체크=isolation. 양식 탭과 공유 상태. */
  selectionMode: 'isolation' | 'deepest';
  onToggleSelectionMode: (next: 'isolation' | 'deepest') => void;
  /** 위젯 자동 배치 — 미배치 위젯 채우기(비파괴, 즉시 실행). */
  onAutoLayoutFill: () => void;
  /** 위젯 자동 배치 — 전체 재구성(파괴, EditorPage 확인 모달 경유). */
  onAutoLayoutRebuild: () => void;
}

// EditorToolbar와 동일한 admin 톤 버튼 클래스(일관 유지). 패널 폭이 좁을 수 있어 줄바꿈 금지(nowrap).
const BTN_BASE =
  'inline-flex shrink-0 items-center gap-1 whitespace-nowrap px-2.5 py-1 text-xs rounded border transition-colors ' +
  'disabled:cursor-not-allowed';
const BTN_GRAY =
  BTN_BASE +
  ' border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:text-gray-300 disabled:bg-white';
const BTN_BRAND =
  BTN_BASE +
  ' border-brand-300 bg-brand-50 text-brand-700 hover:bg-brand-100 disabled:text-gray-300 disabled:border-gray-200 disabled:bg-white';

const DIVIDER = <span className="mx-0.5 h-5 w-px bg-gray-300" aria-hidden />;

/**
 * 작성 탭 편집 툴바. 중앙 미리보기 패널(캔버스) 상단에 얇은 행으로 상시 노출된다.
 * 패널 폭이 좁아지면 줄바꿈 대신 가로 스크롤(overflow-x-auto)로 흡수한다.
 */
export function WriteFormToolbar({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  selectionCount,
  canGroup,
  isGroupSelected,
  onGroup,
  onUngroup,
  onRemove,
  onClearSelection,
  selectionMode,
  onToggleSelectionMode,
  onAutoLayoutFill,
  onAutoLayoutRebuild,
}: WriteFormToolbarProps) {
  const groupEnabled = selectionCount >= 2 && canGroup;
  const hasSelection = selectionCount >= 1;
  // 자동 배치 선택 메뉴 위치(버튼 좌하단 기준) — null이면 닫힘.
  const [autoLayoutMenuPos, setAutoLayoutMenuPos] = useState<{ x: number; y: number } | null>(null);

  return (
    <div
      className="flex shrink-0 items-center gap-1.5 overflow-x-auto border-b border-gray-200 bg-white px-2 py-1.5"
      role="toolbar"
      aria-label="작성 편집 툴바"
    >
      {/* 실행취소 / 다시실행 — 작성 탭 편집도 같은 히스토리 스택 공유(아이콘 위주, title 툴팁) */}
      <button
        type="button"
        onClick={onUndo}
        disabled={!canUndo}
        title="실행 취소 (Ctrl+Z)"
        aria-label="실행 취소"
        className={BTN_GRAY}
      >
        ↶
      </button>
      <button
        type="button"
        onClick={onRedo}
        disabled={!canRedo}
        title="다시 실행 (Ctrl+Shift+Z 또는 Ctrl+Y)"
        aria-label="다시 실행"
        className={BTN_GRAY}
      >
        ↷
      </button>

      {DIVIDER}

      {/* 선택 상태 표시 */}
      <span
        className={`whitespace-nowrap text-xs ${hasSelection ? 'font-medium text-gray-700' : 'text-gray-400'}`}
        aria-live="polite"
      >
        {hasSelection ? `선택 ${selectionCount}개` : '선택 없음'}
      </span>

      {DIVIDER}

      {/* 섹션으로 묶기 — 같은 부모 형제 2개 이상 선택 시 활성 */}
      <button
        type="button"
        onClick={onGroup}
        disabled={!groupEnabled}
        title={
          groupEnabled
            ? '선택한 위젯을 섹션으로 묶기 (Ctrl+G)'
            : '같은 그룹의 형제 위젯을 2개 이상 선택하면 사용할 수 있습니다 (Ctrl+G)'
        }
        className={BTN_BRAND}
      >
        ⊞ 묶기
      </button>

      {/* 섹션 해제 — 단일 섹션 선택 시 활성 */}
      <button
        type="button"
        onClick={onUngroup}
        disabled={!isGroupSelected}
        title={
          isGroupSelected
            ? '섹션을 풀어 자식을 상위로 올리기 (Ctrl+Shift+G)'
            : '섹션(그룹)을 하나 선택하면 사용할 수 있습니다 (Ctrl+Shift+G)'
        }
        className={BTN_GRAY}
      >
        해제
      </button>

      {/* 삭제 — 선택 위젯 일괄 제거 */}
      <button
        type="button"
        onClick={onRemove}
        disabled={!hasSelection}
        title="선택한 위젯 삭제 (Delete)"
        className={BTN_GRAY}
      >
        삭제
      </button>

      {/* 선택 해제 */}
      <button
        type="button"
        onClick={onClearSelection}
        disabled={!hasSelection}
        title="선택 해제 (Esc)"
        className={BTN_GRAY}
      >
        선택 해제
      </button>

      {DIVIDER}

      {/* 위젯 자동 배치 — body 구조에서 결정적 스캐폴드(채우기/전체 재구성 선택 메뉴) */}
      <button
        type="button"
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setAutoLayoutMenuPos({ x: rect.left, y: rect.bottom + 4 });
        }}
        title="양식(body) 구조에서 작성 위젯을 자동 배치합니다 — 보고서 탭에서 데이터 연결·반복 영역을 먼저 마치면 더 깔끔하게 배치됩니다"
        className={BTN_BRAND}
      >
        ⚙ 위젯 자동 배치
      </button>
      <HelpLink topic="writeFormAutoLayout" />
      {autoLayoutMenuPos ? (
        <ComposerContextMenu
          x={autoLayoutMenuPos.x}
          y={autoLayoutMenuPos.y}
          onClose={() => setAutoLayoutMenuPos(null)}
          items={[
            {
              type: 'action',
              label: '미배치 위젯 채우기 — 현재 트리 유지',
              onSelect: onAutoLayoutFill,
            },
            {
              type: 'action',
              label: '전체 재구성 — 양식 구조에서 새로 만들기',
              danger: true,
              onSelect: onAutoLayoutRebuild,
            },
          ]}
        />
      ) : null}

      {/* 선택 방식 토글(우측 정렬) — 양식 탭 EditorToolbar와 같은 상태(selectionMode)를 공유한다.
          체크: 섹션을 단위로 승격 선택(안으로는 더블클릭 드릴, Esc로 상위 섹션으로).
          해제: 클릭한 위젯을 바로 선택하는 이전 방식(더블클릭=편집). */}
      <label
        className="ml-auto flex shrink-0 items-center gap-1 whitespace-nowrap text-xs text-gray-600 cursor-pointer select-none"
        title={
          '체크: 섹션(그룹)을 단위로 승격 선택 — 안으로 들어가려면 더블클릭(드릴), Esc로 상위 섹션으로.\n' +
          '해제: 클릭한 위젯을 바로 선택하는 이전 방식 — 더블클릭=편집.'
        }
      >
        <input
          type="checkbox"
          checked={selectionMode === 'isolation'}
          onChange={(e) => onToggleSelectionMode(e.target.checked ? 'isolation' : 'deepest')}
        />
        그룹 우선 선택
      </label>
    </div>
  );
}
